import uuid from '@vend/vend-uuid'

import { ToastNotifications } from '@lightspeed/design-system-react'
import {
  Deferred,
  getFromStorage,
  saveToStorage,
  removeFromStorage,
} from '@vend/utilities'

/**
 * Responsible for preventing multiple windows.
 *
 * @module duplicateWindowManagerFactory
 */
export function duplicateWindowManagerFactory() {
  const SESSION_KEY = 'VEND_WR_BROWSER_SESSION'
  const TIMESTAMP_KEY = 'VEND_WR_TIMESTAMP'
  const DUPLICATE_MSG =
    'Do you have another sell screen open? You should either close or refresh this window.'

  const updateSessionInterval = 10000
  let _sessionId: string | null = uuid()
  let _duplicateListener: ReturnType<typeof setInterval> | undefined

  // ============================== Private API ============================== //

  /**
   * Set the current time within localstorage.
   *
   * @private
   * @method _setSessionTimeInStorage
   *
   */
  function _setSessionTimeInStorage() {
    saveToStorage(TIMESTAMP_KEY, Date.now().toString())
  }

  /**
   * Retrieve the session time within localstorage.
   *
   * @private
   * @method _getSessionTimeFromStorage
   *
   */
  function _getSessionTimeFromStorage() {
    const storedSessionTime = getFromStorage<string>(TIMESTAMP_KEY)

    if (storedSessionTime) {
      return parseInt(storedSessionTime, 10)
    }
  }

  /**
   * Set a unique browser session id within localstorage.
   *
   * @private
   * @method _setSessionIdInStorage
   *
   */
  function _setSessionIdInStorage() {
    saveToStorage(SESSION_KEY, _sessionId!)
  }

  /**
   * Retrieve the browser session id within localstorage.
   *
   * @private
   * @method _getSessionIdFromStorage
   *
   */
  function _getSessionIdFromStorage() {
    return getFromStorage<string>(SESSION_KEY)
  }

  /**
   * Sets the required items in localstorage.
   *
   * @private
   * @method _setLocalStorageItems
   *
   */
  function _setLocalStorageItems() {
    _setSessionIdInStorage()
    _setSessionTimeInStorage()
  }

  /**
   * Cancels the _duplicateListener interval
   *
   * @private
   * @method _stopDuplicateListener
   */
  function _stopDuplicateListener() {
    if (_duplicateListener) {
      clearInterval(_duplicateListener)
      _duplicateListener = undefined
    }
  }

  /**
   * Removes the session items from localStorage, clears the sessionId and
   * cancels the duplicate window listener
   *
   * @private
   * @method _clearSessionItems
   *
   */
  function _clearSessionItems() {
    _sessionId = null
    removeFromStorage(SESSION_KEY)
    removeFromStorage(TIMESTAMP_KEY)

    _stopDuplicateListener()
  }

  /**
   * Checks the session time in localstorage. If the time difference is greater than updateSessionInterval then
   * we either have a stale window or a window that is asleep - so assume we're the only window.
   *
   * If the time difference is less than updateSessionInterval then either we have another active window or the
   * page has been refreshed and clean up of the localstorage failed.
   *
   * @private
   * @method _confirmSecondWindow
   *
   * @return {Promise<confirmSecondWindowDeferred>} A promise evaluating the presense of another sell screen
   * window
   */
  function _confirmSecondWindow() {
    const confirmSecondWindowDeferred = new Deferred<boolean>()
    const storedTime = _getSessionTimeFromStorage()
    const timeDifference = Date.now() - storedTime!
    const confirmSecondWindowBuffer = 2000
    let secondaryIntervalCheck

    // Time difference is greater than updateSessionInterval then we either have a stale window or a
    // window that is asleep - so assume we're the only window (maybe a false negative).
    if (timeDifference > updateSessionInterval) {
      confirmSecondWindowDeferred.resolve(false)
    } else {
      // Either we have another active window or the page has been refreshed and clean up of the
      // localstorage failed.
      secondaryIntervalCheck = updateSessionInterval - timeDifference

      // If secondary interval check is less than 2 seconds, add a two second buffer to ensure
      // that we don't recheck too soon
      if (secondaryIntervalCheck < 2000) {
        secondaryIntervalCheck += confirmSecondWindowBuffer
      }

      setTimeout(function () {
        const latestStoredTime = _getSessionTimeFromStorage()

        // If the latest stored time is greater than our previously retrieved time, we know there is
        // another window setting values in localStorage
        if (latestStoredTime! >= storedTime!) {
          confirmSecondWindowDeferred.resolve(true)
        } else {
          // Either a previous session was not cleared up correctly, or the other window is asleep. So we
          // assume we are the only window (maybe a false negative)
          confirmSecondWindowDeferred.resolve(false)
        }
      }, secondaryIntervalCheck)
    }
    return confirmSecondWindowDeferred.promise
  }

  /**
   * Initiated when we know the current window is the only window. Check's the browser sessionId in
   * localStorage with that stored in memory. Sets a new timestamp on every updateSessionInterval.
   *
   * @private
   * @method _listenForOtherWindow
   */
  function _listenForOtherWindow() {
    // Clear the required items when page is hidden; using pagehide since beforeunload never gets fired on iOS
    window.addEventListener('pagehide', _clearSessionItems)

    _setLocalStorageItems()

    _duplicateListener = setInterval(function () {
      const currentSessionId = _getSessionIdFromStorage()

      // Handle that rare instance where another window has removed our
      // session details, so prompt the user
      if (currentSessionId !== _sessionId) {
        ToastNotifications.negative(DUPLICATE_MSG)
        _stopDuplicateListener()
      } else {
        _setSessionTimeInStorage()
      }
    }, updateSessionInterval)
  }

  // ============================== Public API ============================== //

  return {
    /**
     * Checks if there is a sessionId within localStorage. If there is not a sessionId, set the required
     * sessionItems and listen for the presence of another window. Otherwise if there is a sessionId, we
     * confirm either the existence of another window, or a failed cleanup.
     *
     * @method ensureSingleWindow
     * @return {Promise} A promise evaluating the presence of another sell screen
     */
    ensureSingleWindow() {
      const ensureSingleWindowDeferred = new Deferred<void>()
      const currentSessionId = _getSessionIdFromStorage()

      if (currentSessionId) {
        _confirmSecondWindow().then(function (isAnotherWindow) {
          // Do we have a second window?
          if (isAnotherWindow) {
            ensureSingleWindowDeferred.reject()
          } else {
            // Either a stale window or a window that is asleep - so assume we're the only window
            // (maybe a false negative).
            _listenForOtherWindow()
            ensureSingleWindowDeferred.resolve()
          }
        })
      } else {
        // No session within localStorage, so resolve and listen for another window
        _listenForOtherWindow()
        ensureSingleWindowDeferred.resolve()
      }
      return ensureSingleWindowDeferred.promise
    },
  }
}

export type DuplicateWindowManager = ReturnType<
  typeof duplicateWindowManagerFactory
>

export const duplicateWindowManager = duplicateWindowManagerFactory()
